import React from 'react'
import { useEvent, useStore } from 'effector-react/ssr'
import styled from 'styled-components'
import { Modal } from '@ui/modal'
import * as model from '@features/music-player/model'
import { Circle } from '@ui/icons/circle'
import { PauseIcon, PlayIcon } from '@ui/icons/player'
import { DownloadIcon } from '@ui/icons/download'
import { $isMobile } from '@features/window-size-observer'
import { Devices } from '@styles'
import styles from './track-modal.module.scss'
import CloseIcon from './close.svg'
import { Histogram } from './histogram'

export const OpenedTrackModal = () => {
  const track = useStore(model.$openedTrack)
  const playingTrackId = useStore(model.$playingTrackId)
  const trackModalIsOpened = useStore(model.$trackModalIsOpened)
  const isMobile = useStore($isMobile)
  const playedPercents = useStore(model.$playedPercents)

  const playClicked = useEvent(model.playClicked)
  const pauseClicked = useEvent(model.pauseClicked)
  const trackModalClosed = useEvent(model.trackModalClosed)

  const onPlayClick = () => {
    if (track === null) {
      return
    }

    playClicked(track.id)
  }

  const onPauseClick = () => {
    if (track === null) {
      return
    }

    pauseClicked(track.id)
  }

  const isOpened = isMobile ? false : trackModalIsOpened

  return (
    <Modal isOpened={isOpened} onClose={trackModalClosed}>
      <Content>
        <Cover style={{ backgroundImage: `url(${track?.coverUrl})` }} />

        <AboutWrapper>
          <About>
            <AboutHeader>
              <Year>{track?.year}</Year>
              <Circle />
              <span>{track?.artist}</span>
            </AboutHeader>

            <Title>{track?.title}</Title>
            <Description>{track?.description}</Description>
          </About>

          <Histogram
            isVisible={trackModalIsOpened && track !== null}
            trackIsPlaying={playingTrackId === track?.id}
            audioSourceUrl={track?.sourceUrl ?? ''}
            progressPercents={playedPercents}
          />

          <Actions>
            <PlayButton
              isPlaying={playingTrackId === track?.id}
              playClicked={onPlayClick}
              pauseClicked={onPauseClick}
            />

            <DownloadLink className={styles.download} href={track?.sourceUrl} target='_blank'>
              <DownloadIcon />
              <span>Скачать</span>
            </DownloadLink>
          </Actions>

          {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
          {/*@ts-ignore*/}
          <CloseButton onClick={trackModalClosed} />
        </AboutWrapper>
      </Content>
    </Modal>
  )
}

const CloseButton = styled(CloseIcon)`
  position: absolute;
  right: 30px;
  top: 30px;

  @media screen and ${Devices.laptop} {
    width: 20px;
    height: 20px;
  }

  @media screen and ${Devices.desktopS} {
    width: 20px;
    height: 20px;
  }

  @media screen and ${Devices.desktopM} {
    width: 32px;
    height: 32px;
  }
`

const Year = styled.span`
  font-size: 18px;
  line-height: 21px;
`

const Description = styled.p`
  font-size: 16px;
  line-height: 19px;
  margin: 0;

  overflow: auto;
  flex-grow: 1;

  margin-top: 20px;
`

const Title = styled.span`
  font-family: 'Magnolia Script', 'SansSerif';
  font-size: 43px;
  line-height: 54px;

  @media screen and ${Devices.desktopM} {
    font-size: 63px;
    line-height: 84px;
  }
`

const AboutWrapper = styled.div`
  display: grid;
  grid-template-rows: 50% 20% 30%;
`

const About = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;

  padding: 50px 50px 0 50px;

  @media screen and ${Devices.desktopM} {
    padding: 130px 50px 0 50px;
  }
`

const AboutHeader = styled.div`
  display: flex;
  align-items: flex-start;
  column-gap: 5px;

  > svg {
    position: relative;
    top: 8px;
  }
`

const Actions = styled.div`
  display: flex;
  align-items: center;

  padding-left: 50px;

  > *:first-child {
    margin-right: 30px;
  }

  > svg {
    height: 80px;

    @media screen and ${Devices.desktopM} {
      height: 80px;
    }
  }
`

const DownloadLink = styled.a`
  display: flex;
  align-items: center;
  column-gap: 7px;

  text-decoration: none;

  > svg,
  > span {
    cursor: pointer;
  }

  > span {
    color: #e469d1;
    font-size: 16px;
    line-height: 19px;
  }
`

const Cover = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;

  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
`

const Content = styled.div`
  --coverWidth: 350px;

  background: white;

  width: 65vw;
  height: 70vh;

  position: relative;
  overflow: hidden;

  display: grid;
  grid-template-columns: var(--coverWidth) 1fr;
  grid-template-rows: 70vh;

  @media screen and ${Devices.desktopS} {
    --coverWidth: 450px;
  }

  > svg {
    cursor: pointer;

    position: absolute;
    top: 30px;
    right: 30px;
  }
`

interface PlayButtonProps {
  isPlaying: boolean
  playClicked: () => void
  pauseClicked: () => void
}

const PlayButton = (props: PlayButtonProps) =>
  props.isPlaying ? <PauseIcon onClick={props.pauseClicked} /> : <PlayIcon onClick={props.playClicked} />
